import React, { useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const DunningTestTool = () => {
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);

  const getCurrentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payments/subscription-status`,
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      const status = response.data.subscription?.billing_status || 'active';
      setCurrentStatus(status);
      return status;
    } catch (error) {
      console.error('Error getting status:', error);
      return null;
    }
  };

  React.useEffect(() => {
    getCurrentStatus();
  }, []);

  const simulateStatus = async (status, daysAgo = 0) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Call test endpoint
      const response = await axios.post(
        `${API}/api/admin/test/simulate-billing-status`,
        { 
          billing_status: status,
          days_ago: daysAgo
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      if (response.data.success) {
        toast.success(`Status auf "${status}" gesetzt! Seite wird neu geladen...`);
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error) {
      console.error('Error simulating status:', error);
      toast.error('Fehler beim Simulieren des Status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">🧪 Dunning-System Test-Tool</h1>
          <p className="text-muted-foreground">
            Simulieren Sie verschiedene Zahlungsstatus, um das Dunning-System zu testen.
          </p>
        </div>

        {/* Current Status */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Aktueller Status</CardTitle>
          </CardHeader>
          <CardContent>
            {currentStatus ? (
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-lg ${
                  currentStatus === 'active' ? 'bg-green-100 dark:bg-green-900/30' :
                  currentStatus === 'past_due' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                  currentStatus === 'unpaid' ? 'bg-orange-100 dark:bg-orange-900/30' :
                  'bg-red-100 dark:bg-red-900/30'
                }`}>
                  {currentStatus === 'active' ? <CheckCircle className="h-6 w-6 text-green-600" /> :
                   currentStatus === 'past_due' ? <AlertCircle className="h-6 w-6 text-yellow-600" /> :
                   currentStatus === 'unpaid' ? <AlertCircle className="h-6 w-6 text-orange-600" /> :
                   <XCircle className="h-6 w-6 text-red-600" />}
                </div>
                <div>
                  <p className="font-semibold text-lg capitalize">{currentStatus}</p>
                  <p className="text-sm text-muted-foreground">
                    {currentStatus === 'active' && 'Alles in Ordnung - keine Zahlungsprobleme'}
                    {currentStatus === 'past_due' && 'Grace Period - Gelbes Banner wird angezeigt'}
                    {currentStatus === 'unpaid' && 'Read-Only Modus - Oranges Banner'}
                    {currentStatus === 'suspended' && 'Account gesperrt - Rotes Overlay'}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">Lade Status...</p>
            )}
          </CardContent>
        </Card>

        {/* Test Buttons */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Zahlungsstatus simulieren</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            
            {/* ACTIVE */}
            <div className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">✅ ACTIVE (Normal)</p>
                <p className="text-sm text-muted-foreground mb-3">
                  Keine Zahlungsprobleme - Kein Banner wird angezeigt
                </p>
                <Button
                  onClick={() => simulateStatus('active', 0)}
                  disabled={loading}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Auf ACTIVE setzen
                </Button>
              </div>
            </div>

            {/* PAST_DUE */}
            <div className="flex items-start gap-3 p-4 border border-yellow-200 dark:border-yellow-800 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/10 transition-colors">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">⚠️ PAST_DUE (Grace Period)</p>
                <p className="text-sm text-muted-foreground mb-3">
                  0-3 Tage überfällig - Gelbes Warning Banner wird oben angezeigt
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => simulateStatus('past_due', 1)}
                    disabled={loading}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    1 Tag überfällig
                  </Button>
                  <Button
                    onClick={() => simulateStatus('past_due', 2)}
                    disabled={loading}
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    2 Tage überfällig
                  </Button>
                </div>
              </div>
            </div>

            {/* UNPAID */}
            <div className="flex items-start gap-3 p-4 border border-orange-200 dark:border-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">🔒 UNPAID (Read-Only)</p>
                <p className="text-sm text-muted-foreground mb-3">
                  4-10 Tage überfällig - Oranges Warning Banner, eingeschränkter Zugriff
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => simulateStatus('unpaid', 5)}
                    disabled={loading}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    5 Tage überfällig
                  </Button>
                  <Button
                    onClick={() => simulateStatus('unpaid', 7)}
                    disabled={loading}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    7 Tage überfällig
                  </Button>
                </div>
              </div>
            </div>

            {/* SUSPENDED */}
            <div className="flex items-start gap-3 p-4 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
              <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">🚫 SUSPENDED (Gesperrt)</p>
                <p className="text-sm text-muted-foreground mb-3">
                  11+ Tage überfällig - Rotes Fullscreen Overlay, kompletter Zugriff blockiert
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => simulateStatus('suspended', 12)}
                    disabled={loading}
                    variant="destructive"
                  >
                    12 Tage überfällig
                  </Button>
                  <Button
                    onClick={() => simulateStatus('suspended', 15)}
                    disabled={loading}
                    variant="destructive"
                  >
                    15 Tage überfällig
                  </Button>
                </div>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              💡 Anleitung
            </h3>
            <ul className="text-sm space-y-1 text-muted-foreground">
              <li>1. Klicken Sie auf einen der Buttons, um einen Status zu simulieren</li>
              <li>2. Die Seite wird automatisch neu geladen</li>
              <li>3. Sie sehen dann den entsprechenden Banner/Overlay</li>
              <li>4. Setzen Sie den Status auf "ACTIVE" zurück, um den Test zu beenden</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
